import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  DataState,
  PageHeader,
  Select,
  StatusBadge,
  Table,
  formatDate,
  type Column,
} from "@vms/ui-web";
import { useOrgContext } from "../../context/OrgContext";
import { useQuery } from "../../api/queries";

interface SessionRow {
  id: string;
  eventId: string;
  driverId: string;
  simulatorId: string | null;
  car: string | null;
  carName?: string | null;
  track: string | null;
  trackName?: string | null;
  status: "pending" | "loading" | "active" | "completed" | "cancelled";
  cancelReason: string | null;
  startedAt: string | null;
  endedAt: string | null;
  createdAt: string;
  /** Server-resolved name fields, populated by the cloud venue-content endpoint. */
  eventName?: string | null;
  driverName?: string | null;
}

export function VenueSessions() {
  const { currentOrg, currentVenue } = useOrgContext();
  const sessions = useQuery<SessionRow[]>(
    currentVenue ? `/api/venues/${currentVenue.id}/sessions` : null,
  );

  const [filterEventId, setFilterEventId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const rows = sessions.data ?? [];

  // Derive event filter options from the loaded sessions so the dropdown
  // always reflects what's actually available — no separate /events fetch.
  const eventOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of rows) {
      if (!map.has(s.eventId)) {
        map.set(s.eventId, s.eventName ?? `${s.eventId.slice(0, 8)}…`);
      }
    }
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [rows]);

  const filtered = useMemo(() => {
    let result = rows;
    if (filterEventId) result = result.filter((s) => s.eventId === filterEventId);
    if (filterStatus) result = result.filter((s) => s.status === filterStatus);
    return result;
  }, [rows, filterEventId, filterStatus]);

  const sessionBase =
    currentOrg && currentVenue
      ? `/org/${currentOrg.slug}/venue/${currentVenue.slug}/sessions`
      : "";
  const eventBase =
    currentOrg && currentVenue
      ? `/org/${currentOrg.slug}/venue/${currentVenue.slug}/events`
      : "";

  const columns: Column<SessionRow>[] = [
    {
      key: "event",
      header: "Event",
      render: (s) => (
        <Link
          to={`${eventBase}/${s.eventId}`}
          className="text-sm text-indigo-600 hover:text-indigo-800"
        >
          {s.eventName ?? `${s.eventId.slice(0, 8)}…`}
        </Link>
      ),
    },
    {
      key: "driver",
      header: "Driver",
      render: (s) => (
        <Link
          to={`${sessionBase}/${s.id}`}
          className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
        >
          {s.driverName ?? `${s.driverId.slice(0, 8)}…`}
        </Link>
      ),
    },
    {
      key: "car",
      header: "Car",
      render: (s) => {
        const raw = s.carName ?? s.car ?? "—";
        const parts = raw.split(" / ");
        return parts.length > 1 ? (
          <>
            {parts[0]} <span className="text-gray-400">/ {parts[1]}</span>
          </>
        ) : (
          raw
        );
      },
    },
    {
      key: "track",
      header: "Track",
      render: (s) => s.trackName ?? s.track ?? "—",
    },
    {
      key: "status",
      header: "Status",
      render: (s) => (
        <div className="flex items-center gap-1">
          <StatusBadge domain="session" status={s.status} />
          {s.cancelReason && (
            <span className="text-xs text-gray-500">· {s.cancelReason}</span>
          )}
        </div>
      ),
    },
    {
      key: "createdAt",
      header: "Created",
      render: (s) => formatDate(s.createdAt),
    },
  ];

  if (!currentVenue) return null;

  const STATUS_FILTER_OPTIONS = [
    { value: "pending", label: "Pending" },
    { value: "loading", label: "Loading" },
    { value: "active", label: "Active" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
  ];

  return (
    <div>
      <PageHeader title="Sessions" subtitle="Per-driver session history at this venue." />

      <div className="flex gap-4 mb-4">
        <div className="w-64">
          <Select
            placeholder="All Events"
            options={eventOptions}
            value={filterEventId}
            onChange={(e) => setFilterEventId(e.target.value)}
          />
        </div>
        <div className="w-48">
          <Select
            placeholder="All Statuses"
            options={STATUS_FILTER_OPTIONS}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          />
        </div>
      </div>

      <DataState
        loading={sessions.loading}
        error={sessions.error}
        empty={!sessions.loading && rows.length === 0}
        emptyMessage="No sessions yet."
      >
        <>
          <p className="text-sm text-gray-500 mb-3">
            {filterEventId || filterStatus
              ? `${filtered.length} of ${rows.length} sessions`
              : `${rows.length} sessions`}
          </p>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <Table<SessionRow>
              columns={columns}
              data={filtered}
              keyExtractor={(s) => s.id}
            />
          </div>
        </>
      </DataState>
    </div>
  );
}
